import { collection } from '@/infra/collection'
import { action, computed } from 'mobx'
import {
  AssumedWorkpaper,
  type AssumedWorkpaperProps,
} from './AssumedWorkpaper'
import {
  BasicInformation,
  type BasicInformationProps,
} from './BasicInformation'
import {
  BusinessActivity,
  type BusinessActivityProps,
} from './BusinessActivity'
import { RentalActivity, type RentalActivityProps } from './RentalActivity'
import { Id } from '@/infra/id'

export interface TaxPreparationDataProps {
  basicInformation: BasicInformationProps
  businesses: BusinessActivityProps[]
  rentals: RentalActivityProps[]
  workpapers: AssumedWorkpaperProps[]
}

export class TaxPreparationData {
  readonly basicInformation = new BasicInformation()

  readonly businesses = collection({
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
    create: () => new BusinessActivity(),
  })

  readonly rentals = collection({
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
    create: () => new RentalActivity(),
  })

  readonly workpapers = collection<
    AssumedWorkpaper,
    string,
    AssumedWorkpaperProps
  >({
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
    create: () => new AssumedWorkpaper(),
  })

  @computed
  get activeBusinesses() {
    return this.businesses.items.filter((business) => !business.archived)
  }

  @computed
  get archivedBusinesses() {
    return this.businesses.items.filter((business) => business.archived)
  }

  @computed
  get activeRentals() {
    return this.rentals.items.filter((rental) => !rental.archived)
  }

  @computed
  get archivedRentals() {
    return this.rentals.items.filter((rental) => rental.archived)
  }

  @action.bound
  set(props: Partial<TaxPreparationDataProps>): this {
    if (props.basicInformation) {
      this.basicInformation.set(props.basicInformation)
    }
    if (props.businesses) {
      this.businesses.reset(props.businesses)
    }
    if (props.rentals) {
      this.rentals.reset(props.rentals)
    }
    if (props.workpapers) {
      this.workpapers.reset(props.workpapers)
    }
    return this
  }

  @action.bound
  archiveRental(rentalId: string): void {
    const rental = this.rentals.get(rentalId)
    if (rental) {
      rental.archive()
      this.unassignWorkpapers('RENTAL', rentalId)
    }
  }

  @action.bound
  archiveBusiness(businessId: string): void {
    const business = this.businesses.get(businessId)
    if (business) {
      business.archive()
      this.unassignWorkpapers('BUSINESS', businessId)
    }
  }

  @action.bound
  archiveDependent(dependentId: string): void {
    const dependent = this.basicInformation.dependents.get(dependentId)
    if (dependent) {
      dependent.archive()
      this.unassignWorkpapers('DEPENDENT', dependentId)
    }
  }

  @action.bound
  removeSpouse(): void {
    const spouseId = this.basicInformation.spouse.id
    this.basicInformation.spouse.clear()

    this.unassignWorkpapers('SPOUSE', Id.assert(spouseId))
    for (const workpaper of this.workpapers.items) {
      if (workpaper.assignedTo.type === 'JOINT') {
        workpaper.assignedTo = {
          type: 'TAX_PAYER',
          id: workpaper.assignedTo.taxPayerId,
        }
      }
    }
  }

  @action.bound
  private unassignWorkpapers(
    assigneeType: 'RENTAL' | 'BUSINESS' | 'DEPENDENT' | 'SPOUSE',
    id: string,
  ): void {
    for (const workpaper of this.workpapers.items) {
      if (
        workpaper.assignedTo.type === assigneeType &&
        workpaper.assignedTo.id === id
      ) {
        workpaper.assignedTo = { type: 'UNASSIGNED' }
      }
    }
  }

  toProps(): TaxPreparationDataProps {
    return {
      basicInformation: this.basicInformation.toProps(),
      businesses: this.businesses.items.map((b) => b.toProps()),
      rentals: this.rentals.items.map((r) => r.toProps()),
      workpapers: this.workpapers.items.map((w) => w.toProps()),
    }
  }
}
